/**
 * LDS Quantity Calculator
 *
 * Règles Métaltop :
 * - besoin brut = surface / rendement * couches
 * - marge de sécurité = +10 %
 * - besoin conseillé = arrondi au 0,5 L supérieur
 * - optimisation des conditionnements disponibles
 * - pour un plan mixte (ex. 5 L + 1 L), ajout des 2 variantes au panier
 *
 * Persistence : localStorage `lds-qty-project-{productId}`
 */

if (!window.Eurus.loadedScript.has('lds-qty-calculator.js')) {
  window.Eurus.loadedScript.add('lds-qty-calculator.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data(
        'xQtyCalculator',
        (
          productId,
          sectionId,
          rendementParLitre,
          packagingLitres,
          couchesDefault
        ) => ({
          showModal: false,
          mode: 'oui',
          superficie: '',
          longueur: '',
          largeur: '',
          couches: couchesDefault,
          project: null,
          productData: null,
          isApplyingPackaging: false,

          get surfaceM2() {
            if (this.mode === 'oui') {
              return parseFloat(this.superficie) || 0;
            }

            return (
              (parseFloat(this.longueur) || 0) *
              (parseFloat(this.largeur) || 0)
            );
          },

          get rawLitresNeeded() {
            if (this.surfaceM2 <= 0 || !rendementParLitre) {
              return 0;
            }

            const userCoats =
              parseInt(this.couches, 10) ||
              parseInt(couchesDefault, 10) ||
              1;

            return (
              this.surfaceM2 /
              rendementParLitre
            ) * userCoats;
          },

          get litresNeeded() {
            if (this.rawLitresNeeded <= 0) {
              return 0;
            }

            const litresAvecMarge =
              this.rawLitresNeeded * 1.10;

            return (
              Math.ceil(
                litresAvecMarge * 2
              ) / 2
            );
          },

          get qtyNeeded() {
            if (
              this.litresNeeded <= 0 ||
              !packagingLitres
            ) {
              return 1;
            }

            return Math.max(
              1,
              Math.ceil(
                this.litresNeeded /
                packagingLitres
              )
            );
          },

          get subtitle() {
            if (!this.project) {
              return 'Sélectionner ici…';
            }

            const plan =
              this.packPlanLabel
                ? ` · ${this.packPlanLabel}`
                : '';

            return (
              `Mon projet : ${this.project.superficie} m²${plan}`
            );
          },

          get packPlanLabel() {
            if (
              !this.project?.packPlan?.items?.length
            ) {
              return '';
            }

            return this.project.packPlan.items
              .map(
                item =>
                  `${item.qty} × ${this._formatLitres(item.litres)}`
              )
              .join(' + ');
          },

          init() {
            this._storageKey =
              `lds-qty-project-${productId}`;

            this._installMixedCartHandler();

            try {
              const saved =
                JSON.parse(
                  localStorage.getItem(
                    this._storageKey
                  )
                );

              if (
                saved &&
                parseFloat(saved.superficie) > 0
              ) {
                this._restoreProject(saved);
              }
            } catch (e) {
              // Ignore localStorage errors.
            }
          },

          openModal() {
            if (this.project) {
              this.mode =
                this.project.mode ||
                'oui';

              this.couches =
                this.project.couches ||
                couchesDefault ||
                1;

              if (this.mode === 'oui') {
                this.superficie =
                  this.project.superficie ||
                  '';
              } else {
                this.longueur =
                  this.project.longueur ||
                  '';

                this.largeur =
                  this.project.largeur ||
                  '';
              }
            }

            this.showModal = true;

            document.body.classList.add(
              'lds-calc-modal-open'
            );
          },

          closeModal() {
            this.showModal = false;

            document.body.classList.remove(
              'lds-calc-modal-open'
            );
          },

          setMode(m) {
            this.mode = m;

            this.superficie = '';
            this.longueur = '';
            this.largeur = '';

            this.couches =
              couchesDefault;
          },

          async validate() {
            if (this.surfaceM2 <= 0) {
              return;
            }

            const project = {
              mode:
                this.mode,

              superficie:
                +this.surfaceM2.toFixed(2),

              couches:
                parseInt(this.couches, 10) ||
                parseInt(couchesDefault, 10) ||
                1,

              qty:
                this.qtyNeeded,

              rawLiters:
                this.rawLitresNeeded.toFixed(2),

              rawLitres:
                this.rawLitresNeeded.toFixed(2),

              liters:
                this.litresNeeded.toFixed(1),

              litres:
                this.litresNeeded.toFixed(1),

              packaging:
                packagingLitres,

              packPlan:
                null
            };

            if (this.mode === 'non') {
              project.longueur =
                parseFloat(
                  this.longueur
                ) || 0;

              project.largeur =
                parseFloat(
                  this.largeur
                ) || 0;
            }

            try {
              project.packPlan =
                await this._buildPackPlan(
                  this.litresNeeded
                );
            } catch (e) {
              console.warn(
                'Métaltop : impossible de calculer le conditionnement conseillé.',
                e
              );
            }

            this._persistProject(
              project
            );

            this.project =
              project;

            await this._applyProject(
              project,
              true
            );

            this.closeModal();
          },

          deleteProject() {
            try {
              localStorage.removeItem(
                this._storageKey
              );
            } catch (e) {}

            this.project = null;

            this.superficie = '';
            this.longueur = '';
            this.largeur = '';

            this.couches =
              couchesDefault;

            this._updatePropertyInput(
              ''
            );

            this.closeModal();
          },

          async _restoreProject(saved) {
            const project = {
              ...saved
            };

            if (
              !project.packPlan?.items?.length
            ) {
              const litres =
                this._projectLitres(
                  project
                );

              if (litres > 0) {
                try {
                  project.packPlan =
                    await this._buildPackPlan(
                      litres
                    );
                } catch (e) {
                  console.warn(
                    'Métaltop : conditionnement conseillé non restauré.',
                    e
                  );
                }
              }
            }

            this.project =
              project;

            this._persistProject(
              project
            );

            await this._applyProject(
              project,
              true
            );
          },

          _projectLitres(project) {
            const superficie =
              parseFloat(
                project.superficie
              ) || 0;

            const couches =
              parseInt(
                project.couches,
                10
              ) ||
              parseInt(
                couchesDefault,
                10
              ) ||
              1;

            if (
              superficie <= 0 ||
              rendementParLitre <= 0
            ) {
              return 0;
            }

            const raw =
              (
                superficie /
                rendementParLitre
              ) *
              couches;

            const avecMarge =
              raw * 1.10;

            return (
              Math.ceil(
                avecMarge * 2
              ) /
              2
            );
          },

          async _applyProject(
            project,
            selectPackaging = false
          ) {
            const litres =
              this._projectLitres(
                project
              );

            if (litres > 0) {
              const raw =
                (
                  (
                    parseFloat(
                      project.superficie
                    ) || 0
                  ) /
                  rendementParLitre
                ) *
                (
                  parseInt(
                    project.couches,
                    10
                  ) ||
                  parseInt(
                    couchesDefault,
                    10
                  ) ||
                  1
                );

              project.rawLiters =
                raw.toFixed(2);

              project.rawLitres =
                raw.toFixed(2);

              project.liters =
                litres.toFixed(1);

              project.litres =
                litres.toFixed(1);
            }

            if (
              selectPackaging &&
              project.packPlan?.items?.length
            ) {
              await this._selectRecommendedPackaging(
                project
              );

              return;
            }

            const qty =
              this._qtyForCurrentPackaging(
                project
              );

            project.qty =
              qty;

            project.packaging =
              packagingLitres;

            this.project =
              project;

            this._persistProject(
              project
            );

            this.$nextTick(() => {
              this._applyQuantityEverywhere(
                qty
              );
            });

            this._updatePropertyInput(
              `Mon projet: ${Number(
                project.superficie
              )
                .toFixed(2)
                .replace('.', ',')} m²${
                  this.packPlanLabel
                    ? ` - ${this.packPlanLabel}`
                    : ''
                }`
            );
          },

          _qtyForCurrentPackaging(
            project
          ) {
            const currentPack =
              parseFloat(
                packagingLitres
              ) || 0;

            if (
              project.packPlan?.items?.length &&
              currentPack > 0
            ) {
              const matching =
                project.packPlan.items.find(
                  item =>
                    Math.abs(
                      parseFloat(
                        item.litres
                      ) -
                      currentPack
                    ) <
                    0.001
                );

              if (matching) {
                return Math.max(
                  1,
                  parseInt(
                    matching.qty,
                    10
                  ) || 1
                );
              }
            }

            const litres =
              parseFloat(
                project.litres ||
                project.liters
              ) || 0;

            if (
              litres > 0 &&
              currentPack > 0
            ) {
              return Math.max(
                1,
                Math.ceil(
                  litres /
                  currentPack
                )
              );
            }

            return Math.max(
              1,
              parseInt(
                project.qty,
                10
              ) || 1
            );
          },

          async _getProductData() {
            if (this.productData) {
              return this.productData;
            }

            const url =
              this._productUrl();

            const response =
              await fetch(
                `${url}.js`,
                {
                  credentials:
                    'same-origin'
                }
              );

            if (!response.ok) {
              throw new Error(
                'Impossible de lire les variantes du produit.'
              );
            }

            this.productData =
              await response.json();

            return this.productData;
          },

          _packagingOptionIndex(
            productData
          ) {
            const options =
              Array.isArray(
                productData?.options
              )
                ? productData.options
                : [];

            return options.findIndex(
              option => {
                const name =
                  typeof option ===
                  'string'
                    ? option
                    : option?.name;

                return (
                  this._normalize(
                    name
                  ) ===
                  'conditionnement'
                );
              }
            );
          },

          _availablePackSizesFromDom() {
            const root =
              document.getElementById(
                `variant-update-${sectionId}`
              );

            if (!root) {
              return [];
            }

            const select =
              root.querySelector(
                'select.opt-conditionnement'
              );

            if (select) {
              return [
                ...select.options
              ]
                .map(
                  option =>
                    this._parseLitres(
                      option.value ||
                      option.textContent
                    )
                )
                .filter(
                  value =>
                    value > 0
                );
            }

            const fieldset =
              [
                ...root.querySelectorAll(
                  'fieldset.product-form__input'
                )
              ].find(
                el =>
                  this._normalize(
                    el.dataset.optionName
                  ) ===
                  'conditionnement'
              );

            if (!fieldset) {
              return [];
            }

            return [
              ...fieldset.querySelectorAll(
                'input[type="radio"]'
              )
            ]
              .map(
                input =>
                  this._parseLitres(
                    input.value
                  )
              )
              .filter(
                value =>
                  value > 0
              );
          },

          async _availablePackSizes() {
            let sizes =
              this._availablePackSizesFromDom();

            if (!sizes.length) {
              const productData =
                await this._getProductData();

              const packIndex =
                this._packagingOptionIndex(
                  productData
                );

              if (packIndex >= 0) {
                const set =
                  new Set();

                (
                  productData.variants ||
                  []
                ).forEach(
                  variant => {
                    const litres =
                      this._parseLitres(
                        variant.options?.[
                          packIndex
                        ]
                      );

                    if (litres > 0) {
                      set.add(
                        litres
                      );
                    }
                  }
                );

                sizes =
                  [...set];
              }
            }

            return [
              ...new Set(
                sizes
              )
            ]
              .filter(
                value =>
                  value > 0
              )
              .sort(
                (a, b) =>
                  a - b
              );
          },

          async _buildPackPlan(
            litresNeeded
          ) {
            const need =
              parseFloat(
                litresNeeded
              ) || 0;

            if (need <= 0) {
              return null;
            }

            let packs =
              await this._availablePackSizes();

            if (!packs.length) {
              return null;
            }

            /*
             * Petit pot < 1 L :
             * uniquement pour les besoins
             * <= 0,40 L.
             */
            if (
              need > 0.40 &&
              packs.some(
                pack =>
                  pack >= 1
              )
            ) {
              packs =
                packs.filter(
                  pack =>
                    pack >= 1
                );
            }

            if (!packs.length) {
              return null;
            }

            /*
             * Règle des 80 % :
             *
             * 4,2 L -> 5 L
             * 9 L -> 10 L
             * 13 L -> 15 L
             */
            const singleLarger =
              packs.find(
                pack =>
                  pack >= need
              );

            if (
              singleLarger &&
              need >=
                singleLarger *
                0.80
            ) {
              return {
                litresNeeded:
                  need,

                totalLitres:
                  singleLarger,

                surplus:
                  +(
                    singleLarger -
                    need
                  ).toFixed(3),

                totalPots:
                  1,

                items: [
                  {
                    litres:
                      singleLarger,

                    qty:
                      1
                  }
                ]
              };
            }

            /*
             * Sinon :
             * 1. surplus minimal
             * 2. nombre de pots minimal
             *
             * Exemples :
             * 1,5 L -> 2 x 1 L
             * 2,5 L -> 3 x 1 L
             * 6 L -> 5 L + 1 L
             */
            const scale =
              100;

            const packUnits =
              packs
                .map(
                  pack => ({
                    litres:
                      pack,

                    units:
                      Math.max(
                        1,
                        Math.round(
                          pack *
                          scale
                        )
                      )
                  })
                )
                .sort(
                  (a, b) =>
                    b.units -
                    a.units
                );

            const targetUnits =
              Math.ceil(
                need *
                scale -
                1e-9
              );

            const maxPackUnits =
              Math.max(
                ...packUnits.map(
                  pack =>
                    pack.units
                )
              );

            const maxUnits =
              targetUnits +
              maxPackUnits;

            const dp =
              new Array(
                maxUnits + 1
              ).fill(null);

            dp[0] = {
              pots:
                0,

              counts:
                new Array(
                  packUnits.length
                ).fill(0)
            };

            for (
              let amount = 0;
              amount <= maxUnits;
              amount += 1
            ) {
              const state =
                dp[amount];

              if (!state) {
                continue;
              }

              packUnits.forEach(
                (
                  pack,
                  index
                ) => {
                  const next =
                    amount +
                    pack.units;

                  if (
                    next >
                    maxUnits
                  ) {
                    return;
                  }

                  const nextPots =
                    state.pots +
                    1;

                  if (
                    !dp[next] ||
                    nextPots <
                      dp[next].pots
                  ) {
                    const counts =
                      [
                        ...state.counts
                      ];

                    counts[index] +=
                      1;

                    dp[next] = {
                      pots:
                        nextPots,

                      counts:
                        counts
                    };
                  }
                }
              );
            }

            let chosenUnits =
              -1;

            for (
              let amount =
                targetUnits;
              amount <= maxUnits;
              amount += 1
            ) {
              if (dp[amount]) {
                chosenUnits =
                  amount;

                break;
              }
            }

            if (
              chosenUnits < 0
            ) {
              return null;
            }

            const chosen =
              dp[chosenUnits];

            const items =
              [];

            chosen.counts.forEach(
              (
                qty,
                index
              ) => {
                if (!qty) {
                  return;
                }

                items.push({
                  litres:
                    packUnits[
                      index
                    ].litres,

                  qty:
                    qty
                });
              }
            );

            const totalLitres =
              chosenUnits /
              scale;

            return {
              litresNeeded:
                need,

              totalLitres:
                totalLitres,

              surplus:
                +(
                  totalLitres -
                  need
                ).toFixed(3),

              totalPots:
                chosen.pots,

              items:
                items.sort(
                  (a, b) =>
                    b.litres -
                    a.litres
                )
            };
          },

          async _selectRecommendedPackaging(
            project
          ) {
            if (
              this.isApplyingPackaging
            ) {
              return;
            }

            if (
              !project.packPlan?.items?.length
            ) {
              await this._applyProject(
                project,
                false
              );

              return;
            }

            /*
             * Si plusieurs pots sont nécessaires,
             * on affiche le plus grand
             * conditionnement dans le sélecteur.
             *
             * Exemple 6 L :
             * affichage 5 L
             * panier = 5 L + 1 L
             */
            const displayItem =
              [
                ...project
                  .packPlan
                  .items
              ]
                .sort(
                  (a, b) =>
                    b.litres -
                    a.litres
                )[0];

            if (!displayItem) {
              await this._applyProject(
                project,
                false
              );

              return;
            }

            const changed =
              this._setPackagingControl(
                displayItem.litres
              );

            project.packaging =
              displayItem.litres;

            project.qty =
              displayItem.qty;

            this.project =
              project;

            this._persistProject(
              project
            );

            this._updatePropertyInput(
              `Mon projet: ${Number(
                project.superficie
              )
                .toFixed(2)
                .replace('.', ',')} m²${
                  this.packPlanLabel
                    ? ` - ${this.packPlanLabel}`
                    : ''
                }`
            );

            const recommendedQty =
              Math.max(
                1,
                parseInt(
                  displayItem.qty,
                  10
                ) || 1
              );

            if (changed) {
              this.isApplyingPackaging =
                true;

              /*
               * Le thème Eurus recharge
               * certains éléments après
               * changement de variante.
               */
              window.setTimeout(
                () => {
                  this._applyQuantityEverywhere(
                    recommendedQty
                  );

                  this.isApplyingPackaging =
                    false;
                },
                700
              );

              return;
            }

            this.$nextTick(
              () => {
                this._applyQuantityEverywhere(
                  recommendedQty
                );
              }
            );
          },

          _setPackagingControl(
            litres
          ) {
            const root =
              document.getElementById(
                `variant-update-${sectionId}`
              );

            if (!root) {
              return false;
            }

            const target =
              parseFloat(
                litres
              );

            if (!(target > 0)) {
              return false;
            }

            /*
             * Conditionnement sous forme
             * de menu déroulant.
             */
            const select =
              root.querySelector(
                'select.opt-conditionnement'
              );

            if (select) {
              const option =
                [
                  ...select.options
                ].find(
                  item =>
                    Math.abs(
                      this._parseLitres(
                        item.value ||
                        item.textContent
                      ) -
                      target
                    ) <
                    0.001
                );

              if (!option) {
                return false;
              }

              if (
                select.value !==
                option.value
              ) {
                select.value =
                  option.value;

                select.dispatchEvent(
                  new Event(
                    'change',
                    {
                      bubbles:
                        true
                    }
                  )
                );

                return true;
              }

              return false;
            }

            /*
             * Conditionnement sous forme
             * de boutons radio.
             */
            const fieldset =
              [
                ...root.querySelectorAll(
                  'fieldset.product-form__input'
                )
              ].find(
                el =>
                  this._normalize(
                    el.dataset.optionName
                  ) ===
                  'conditionnement'
              );

            if (!fieldset) {
              return false;
            }

            const radio =
              [
                ...fieldset.querySelectorAll(
                  'input[type="radio"]'
                )
              ].find(
                input =>
                  Math.abs(
                    this._parseLitres(
                      input.value
                    ) -
                    target
                  ) <
                  0.001
              );

            if (!radio) {
              return false;
            }

            if (!radio.checked) {
              radio.checked =
                true;

              radio.dispatchEvent(
                new Event(
                  'change',
                  {
                    bubbles:
                      true
                  }
                )
              );

              return true;
            }

            return false;
          },

          /*
           * Gestion des plans mixtes.
           *
           * Exemple :
           * besoin = 6 L
           * plan = 1 x 5 L + 1 x 1 L
           *
           * Le formulaire Shopify normal
           * ne peut ajouter qu'une variante.
           * On intercepte donc le bouton
           * uniquement lorsque le plan
           * contient plusieurs conditionnements.
           */
          _installMixedCartHandler() {
            window.__ldsQtyMixedHandlers =
              window.__ldsQtyMixedHandlers ||
              {};

            const registryKey =
              `${productId}-${sectionId}`;

            if (
              window.__ldsQtyMixedHandlers[
                registryKey
              ]
            ) {
              return;
            }

            const storageKey =
              this._storageKey;

            const expectedSectionId =
              String(
                sectionId
              );

            const expectedProductUrl =
              this._productUrl();

            const handler =
              async event => {
                const button =
                  event.target?.closest?.(
                    `#x-atc-button-${CSS.escape(
                      expectedSectionId
                    )}`
                  );

                if (
                  !button ||
                  button.disabled
                ) {
                  return;
                }

                let project;

                try {
                  project =
                    JSON.parse(
                      localStorage.getItem(
                        storageKey
                      )
                    );
                } catch (e) {
                  return;
                }

                /*
                 * Un seul conditionnement :
                 * on laisse Eurus travailler normalement.
                 */
                if (
                  !project?.packPlan?.items ||
                  project.packPlan.items.length <= 1
                ) {
                  return;
                }

                /*
                 * Plusieurs conditionnements :
                 * on prend la main.
                 */
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                if (
                  button.dataset.ldsMixedLoading ===
                  '1'
                ) {
                  return;
                }

                button.dataset.ldsMixedLoading =
                  '1';

                button.setAttribute(
                  'aria-busy',
                  'true'
                );

                try {
                  /*
                   * Variante réellement sélectionnée.
                   *
                   * L'URL est prioritaire car Eurus
                   * la met à jour avec ?variant=...
                   */
                  const urlVariantId =
                    parseInt(
                      new URL(
                        window.location.href
                      ).searchParams.get(
                        'variant'
                      ),
                      10
                    );

                  const hiddenVariant =
                    document.getElementById(
                      `update-variant-${expectedSectionId}`
                    );

                  const hiddenVariantId =
                    parseInt(
                      hiddenVariant?.value,
                      10
                    );

                  const currentVariantId =
                    urlVariantId ||
                    hiddenVariantId;

                  if (!currentVariantId) {
                    throw new Error(
                      'Impossible de lire la variante sélectionnée.'
                    );
                  }

                  /*
                   * IMPORTANT :
                   *
                   * On ne passe plus par /products/handle.js
                   * pour chercher la couleur.
                   *
                   * On demande à Shopify de rendre la section
                   * pour la variante réellement sélectionnée.
                   *
                   * Shopify fournit alors les variantes exactes
                   * des différents conditionnements pour cette
                   * même couleur.
                   */
                  let variantRoot =
                    null;

                  try {
                    const separator =
                      expectedProductUrl.includes('?')
                        ? '&'
                        : '?';

                    const sectionResponse =
                      await fetch(
                        `${expectedProductUrl}${separator}variant=${encodeURIComponent(
                          currentVariantId
                        )}&section_id=${encodeURIComponent(
                          expectedSectionId
                        )}`,
                        {
                          credentials:
                            'same-origin',

                          headers: {
                            'Accept':
                              'text/html'
                          },

                          cache:
                            'no-store'
                        }
                      );

                    if (sectionResponse.ok) {
                      const sectionHtml =
                        await sectionResponse.text();

                      const parsedDocument =
                        new DOMParser()
                          .parseFromString(
                            sectionHtml,
                            'text/html'
                          );

                      variantRoot =
                        parsedDocument
                          .getElementById(
                            `variant-update-${expectedSectionId}`
                          );
                    }
                  } catch (e) {
                    variantRoot =
                      null;
                  }

                  /*
                   * Sécurité :
                   * si Shopify ne renvoie pas la section,
                   * on utilise le DOM déjà affiché.
                   */
                  if (!variantRoot) {
                    variantRoot =
                      document.getElementById(
                        `variant-update-${expectedSectionId}`
                      );
                  }

                  if (!variantRoot) {
                    throw new Error(
                      'Le sélecteur de variantes est introuvable.'
                    );
                  }

                  /*
                   * Recherche de l'option Conditionnement.
                   */
                  const packagingContainer =
                    [
                      ...variantRoot.querySelectorAll(
                        '[data-option-name]'
                      )
                    ].find(
                      element =>
                        this._normalize(
                          element.dataset.optionName
                        ) ===
                        'conditionnement'
                    );

                  if (!packagingContainer) {
                    throw new Error(
                      'Option Conditionnement introuvable.'
                    );
                  }

                  /*
                   * Propriétés privées du calculateur.
                   * Exemple : _Mon projet.
                   */
                  const form =
                    document.getElementById(
                      `product-form-${expectedSectionId}`
                    );

                  const properties =
                    {};

                  if (form) {
                    const formData =
                      new FormData(
                        form
                      );

                    for (
                      const [
                        key,
                        value
                      ] of
                      formData.entries()
                    ) {
                      const match =
                        key.match(
                          /^properties\[(.+)\]$/
                        );

                      if (
                        match &&
                        value !== ''
                      ) {
                        properties[
                          match[1]
                        ] =
                          value;
                      }
                    }
                  }

                  /*
                   * Retrouve la variante exacte
                   * correspondant à un conditionnement.
                   */
                  const getVariantForPack =
                    targetLitres => {
                      let optionElement =
                        null;

                      /*
                       * Conditionnement sous forme
                       * de select.
                       */
                      const select =
                        packagingContainer.matches(
                          'select'
                        )
                          ? packagingContainer
                          : packagingContainer.querySelector(
                              'select'
                            );

                      if (select) {
                        optionElement =
                          [
                            ...select.options
                          ].find(
                            option =>
                              Math.abs(
                                this._parseLitres(
                                  option.value ||
                                  option.textContent
                                ) -
                                targetLitres
                              ) <
                              0.001
                          );
                      }

                      /*
                       * Conditionnement sous forme
                       * de boutons radio.
                       */
                      if (!optionElement) {
                        const fieldset =
                          packagingContainer.matches(
                            'fieldset'
                          )
                            ? packagingContainer
                            : packagingContainer.querySelector(
                                'fieldset'
                              );

                        if (fieldset) {
                          optionElement =
                            [
                              ...fieldset.querySelectorAll(
                                'input[type="radio"]'
                              )
                            ].find(
                              input =>
                                Math.abs(
                                  this._parseLitres(
                                    input.value
                                  ) -
                                  targetLitres
                                ) <
                                0.001
                            );
                        }
                      }

                      /*
                       * Fallback spécifique au select
                       * Conditionnement Métaltop.
                       */
                      if (!optionElement) {
                        const directSelect =
                          variantRoot.querySelector(
                            'select.opt-conditionnement'
                          );

                        if (directSelect) {
                          optionElement =
                            [
                              ...directSelect.options
                            ].find(
                              option =>
                                Math.abs(
                                  this._parseLitres(
                                    option.value ||
                                    option.textContent
                                  ) -
                                  targetLitres
                                ) <
                                0.001
                            );
                        }
                      }

                      /*
                       * Fallback radio.
                       */
                      if (!optionElement) {
                        const fieldset =
                          [
                            ...variantRoot.querySelectorAll(
                              'fieldset.product-form__input'
                            )
                          ].find(
                            element =>
                              this._normalize(
                                element.dataset.optionName
                              ) ===
                              'conditionnement'
                          );

                        if (fieldset) {
                          optionElement =
                            [
                              ...fieldset.querySelectorAll(
                                'input[type="radio"]'
                              )
                            ].find(
                              input =>
                                Math.abs(
                                  this._parseLitres(
                                    input.value
                                  ) -
                                  targetLitres
                                ) <
                                0.001
                            );
                        }
                      }

                      if (!optionElement) {
                        return null;
                      }

                      const optionValueId =
                        optionElement.dataset
                          .optionValueId;

                      const resourceId =
                        optionElement.id;

                      const scripts =
                        [
                          ...variantRoot.querySelectorAll(
                            'script[type="application/json"]'
                          )
                        ];

                      /*
                       * product-variant-options.liquid
                       * fournit un JSON pour chaque
                       * valeur d'option.
                       */
                      const variantScript =
                        scripts.find(
                          script =>
                            (
                              optionValueId &&
                              script.dataset
                                .optionValueId ===
                                optionValueId
                            ) ||
                            (
                              resourceId &&
                              script.dataset.resource ===
                                resourceId
                            )
                        );

                      if (
                        !variantScript?.textContent
                      ) {
                        return null;
                      }

                      try {
                        const variant =
                          JSON.parse(
                            variantScript.textContent
                          );

                        return variant?.id
                          ? variant
                          : null;
                      } catch (e) {
                        return null;
                      }
                    };

                  /*
                   * Construction des lignes panier.
                   */
                  const items =
                    [];

                  for (
                    const planItem of
                    project.packPlan.items
                  ) {
                    const targetLitres =
                      parseFloat(
                        planItem.litres
                      );

                    const variant =
                      getVariantForPack(
                        targetLitres
                      );

                    if (!variant) {
                      throw new Error(
                        `Le conditionnement ${this._formatLitres(
                          targetLitres
                        )} n’a pas pu être associé à la couleur sélectionnée.`
                      );
                    }

                    if (
                      variant.available ===
                      false
                    ) {
                      throw new Error(
                        `Le conditionnement ${this._formatLitres(
                          targetLitres
                        )} n’est pas disponible pour la couleur choisie.`
                      );
                    }

                    items.push({
                      id:
                        variant.id,

                      quantity:
                        Math.max(
                          1,
                          parseInt(
                            planItem.qty,
                            10
                          ) || 1
                        ),

                      ...(
                        Object.keys(
                          properties
                        ).length
                          ? {
                              properties:
                                properties
                            }
                          : {}
                      )
                    });
                  }

                  /*
                   * Ajout groupé au panier avec
                   * le mécanisme Eurus déjà validé.
                   */
                  const cartHelper =
                    window.Alpine &&
                    Alpine.store(
                      'xCartHelper'
                    );

                  if (!cartHelper) {
                    throw new Error(
                      'Le gestionnaire panier Eurus est indisponible.'
                    );
                  }

                  await cartHelper
                    .waitForCartUpdate();

                  window.updatingCart =
                    true;

                  try {
                    const sections =
                      cartHelper
                        .getSectionsToRender()
                        .map(
                          section =>
                            section.id
                        );

                    const addResponse =
                      await fetch(
                        Shopify.routes.root +
                        'cart/add.js',
                        {
                          method:
                            'POST',

                          headers: {
                            'Content-Type':
                              'application/json',

                            'Accept':
                              'application/json'
                          },

                          body:
                            JSON.stringify({
                              items:
                                items,

                              sections:
                                sections,

                              sections_url:
                                window.location.pathname
                            })
                        }
                      );

                    if (!addResponse.ok) {
                      const body =
                        await addResponse
                          .json()
                          .catch(
                            () => ({})
                          );

                      throw new Error(
                        body.description ||
                        body.message ||
                        'Shopify n’a pas pu ajouter les conditionnements au panier.'
                      );
                    }

                    const cartData =
                      await addResponse.json();

                    /*
                     * On reconstruit le drawer
                     * AVANT son ouverture.
                     */
                    await new Promise(
                      resolve => {
                        requestAnimationFrame(
                          () => {
                            setTimeout(
                              async () => {
                                if (
                                  cartData.sections
                                ) {
                                  await cartHelper
                                    .reRenderSections(
                                      cartData.sections
                                    );
                                } else {
                                  await cartHelper
                                    .reRenderSections();
                                }

                                const bubble =
                                  document.getElementById(
                                    'cart-icon-bubble'
                                  );

                                if (bubble) {
                                  const count =
                                    parseInt(
                                      bubble.textContent,
                                      10
                                    );

                                  if (
                                    !Number.isNaN(
                                      count
                                    )
                                  ) {
                                    cartHelper
                                      .currentItemCount =
                                      count;
                                  }
                                }

                                resolve();
                              },
                              0
                            );
                          }
                        );
                      }
                    );

                    window.updatingCart =
                      false;

                    document.dispatchEvent(
                      new CustomEvent(
                        'eurus:cart:items-changed'
                      )
                    );

                    /*
                     * Ouverture du panier seulement
                     * une fois son HTML actualisé.
                     */
                    const miniCart =
                      window.Alpine &&
                      Alpine.store(
                        'xMiniCart'
                      );

                    if (miniCart) {
                      miniCart.openCart();
                    }

                  } finally {
                    window.updatingCart =
                      false;
                  }

                } catch (error) {
                  console.error(
                    'Métaltop - ajout multi-conditionnements :',
                    error
                  );

                  alert(
                    error.message ||
                    'Impossible d’ajouter les conditionnements conseillés au panier.'
                  );

                } finally {
                  delete button
                    .dataset
                    .ldsMixedLoading;

                  button.removeAttribute(
                    'aria-busy'
                  );
                }
              };

            document.addEventListener(
              'click',
              handler,
              true
            );

            window.__ldsQtyMixedHandlers[
              registryKey
            ] =
              handler;
          },

          _applyQuantityEverywhere(
            qty
          ) {
            const safeQty =
              Math.max(
                1,
                parseInt(
                  qty,
                  10
                ) || 1
              );

            try {
              this.$dispatch(
                'lds-qty-project-applied',
                {
                  qty:
                    safeQty
                }
              );
            } catch (e) {}

            /*
             * Événement natif du thème Eurus.
             */
            document.dispatchEvent(
              new CustomEvent(
                `eurus:product:quantity-changed-${sectionId}`,
                {
                  detail: {
                    quantity:
                      safeQty
                  }
                }
              )
            );

            /*
             * Synchronisation directe
             * des éventuels champs quantité.
             */
            [
              `Quantity-${sectionId}`,
              `Quantity-atc-${sectionId}`
            ].forEach(
              id => {
                const input =
                  document.getElementById(
                    id
                  );

                if (!input) {
                  return;
                }

                input.value =
                  String(
                    safeQty
                  );

                input.dispatchEvent(
                  new Event(
                    'input',
                    {
                      bubbles:
                        true
                    }
                  )
                );

                input.dispatchEvent(
                  new Event(
                    'change',
                    {
                      bubbles:
                        true
                    }
                  )
                );
              }
            );
          },

          _persistProject(
            project
          ) {
            try {
              localStorage.setItem(
                this._storageKey,
                JSON.stringify(
                  project
                )
              );
            } catch (e) {}
          },

          _updatePropertyInput(
            value
          ) {
            const el =
              document.getElementById(
                `lds-calc-property-${sectionId}`
              );

            if (!el) {
              return;
            }

            if (value) {
              el.value =
                value;

              el.disabled =
                false;
            } else {
              el.value =
                '';

              el.disabled =
                true;
            }
          },

          _productUrl() {
            const picker =
              document.querySelector(
                `#x-product-template-${productId}-${sectionId} .variant-selects-main[data-url]`
              );

            return String(
              picker?.dataset?.url ||
              window.location.pathname
            )
              .split('?')[0]
              .replace(
                /\.js$/,
                ''
              );
          },

          _parseLitres(
            value
          ) {
            const text =
              String(
                value ??
                ''
              )
                .toLowerCase()
                .replace(
                  ',',
                  '.'
                )
                .replace(
                  /\s+/g,
                  ' '
                )
                .trim();

            if (!text) {
              return 0;
            }

            const match =
              text.match(
                /([0-9]+(?:\.[0-9]+)?)/
              );

            if (!match) {
              return 0;
            }

            const number =
              parseFloat(
                match[1]
              );

            if (!(number > 0)) {
              return 0;
            }

            if (
              /\bml\b/.test(
                text
              )
            ) {
              return (
                number /
                1000
              );
            }

            return number;
          },

          _formatLitres(
            value
          ) {
            const litres =
              parseFloat(
                value
              ) || 0;

            if (
              litres > 0 &&
              litres < 1
            ) {
              return (
                `${Math.round(
                  litres *
                  1000
                )} ml`
              );
            }

            return (
              `${String(
                litres
              ).replace(
                '.',
                ','
              )} L`
            );
          },

          _normalize(
            value
          ) {
            return String(
              value ??
              ''
            )
              .toLowerCase()
              .normalize(
                'NFD'
              )
              .replace(
                /[\u0300-\u036f]/g,
                ''
              )
              .replace(
                /\s+/g,
                ' '
              )
              .trim();
          }
        })
      );
    });
  });
}